<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AssistantAPI from 'dashboard/api/assistant';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AssistantPageLayout from '../components/AssistantPageLayout.vue';
import AssistantSection from '../components/AssistantSection.vue';
import AssistantFormTabs from '../components/AssistantFormTabs.vue';
import PictureLibrary from '../components/PictureLibrary.vue';
import AssistantLoadError from '../components/AssistantLoadError.vue';
import { usePersistentRef } from '../composables/useAssistantCache';

const { t } = useI18n();

const faqs = usePersistentRef('faqs', []);
const suggestions = usePersistentRef('faq-suggestions', []);
const documents = usePersistentRef('documents', []);
// Lists may be legitimately empty, so a flag (not emptiness) marks
// "loaded at least once" — skeleton only before the first load.
const knowledgeLoaded = usePersistentRef('knowledge-loaded', false);
const newFaq = ref({ question: '', answer: '' });
const newDocument = ref({ sourceRef: '', text: '' });
const websiteUrl = ref('');
const isFetching = ref(!knowledgeLoaded.value);
const isBusy = ref(false);
const loadError = ref(false);

const faqQuery = ref('');
const docQuery = ref('');

// Dialog handles + newly-added highlighting
const faqDialog = ref(null);
const docDialog = ref(null);
const flashFaqIds = ref(new Set());
const flashDocKeys = ref(new Set());
// Keys of documents added this session, newest first — keeps them on top
// even though the API has no created-at field to sort by.
const recentDocKeys = ref([]);

const docKey = doc => `${doc.source_type}:${doc.source_ref}`;

// FAQ form modes: one-at-a-time vs bulk paste
const faqTabs = computed(() => [
  { label: t('ASSISTANT.KNOWLEDGE.FAQ_TAB_SINGLE') },
  { label: t('ASSISTANT.KNOWLEDGE.FAQ_TAB_BULK') },
]);
const faqMode = ref(0);
const bulkFaqText = ref('');

// Document form modes: upload a file, paste text, or import a website
const docTabs = computed(() => [
  { label: t('ASSISTANT.KNOWLEDGE.DOC_TAB_UPLOAD') },
  { label: t('ASSISTANT.KNOWLEDGE.DOC_TAB_PASTE') },
  { label: t('ASSISTANT.KNOWLEDGE.DOC_TAB_WEBSITE') },
]);
const docMode = ref(0);
const fileInput = ref(null);
const isReadingFile = ref(false);

const parseBulkFaqs = text => {
  const pairs = [];
  let current = null;
  let field = null;
  text.split(/\r?\n/).forEach(line => {
    const question = line.match(/^\s*Q\s*[:.]\s*(.*)$/i);
    const answer = line.match(/^\s*A\s*[:.]\s*(.*)$/i);
    if (question) {
      if (current?.question && current?.answer) pairs.push(current);
      current = { question: question[1].trim(), answer: '' };
      field = 'question';
    } else if (answer && current) {
      current.answer = answer[1].trim();
      field = 'answer';
    } else if (current && field && line.trim()) {
      current[field] = `${current[field]}\n${line.trim()}`.trim();
    }
  });
  if (current?.question && current?.answer) pairs.push(current);
  return pairs.filter(pair => pair.question && pair.answer);
};

const bulkFaqCount = computed(() => parseBulkFaqs(bulkFaqText.value).length);

const bulkHintText = computed(() => {
  const hint = t('ASSISTANT.KNOWLEDGE.BULK_HINT');
  if (!bulkFaqCount.value) return hint;
  return `${hint} · ${t('ASSISTANT.KNOWLEDGE.BULK_COUNT', {
    count: bulkFaqCount.value,
  })}`;
});

const sourceIcon = sourceType => {
  if (sourceType === 'url' || sourceType === 'website') {
    return 'i-ph-link-simple';
  }
  return 'i-ph-file-text';
};

// Newest first: the Brain returns FAQs in ascending id order.
const faqsSorted = computed(() => [...faqs.value].sort((a, b) => b.id - a.id));

// Documents have no id/date, so surface this session's additions first,
// then fall back to the server order (sort is stable for equal ranks).
const documentsSorted = computed(() => {
  const order = recentDocKeys.value;
  return [...documents.value].sort((a, b) => {
    const rankA = order.indexOf(docKey(a));
    const rankB = order.indexOf(docKey(b));
    return (
      (rankA === -1 ? Infinity : rankA) - (rankB === -1 ? Infinity : rankB)
    );
  });
});

const filteredFaqs = computed(() => {
  const query = faqQuery.value.trim().toLowerCase();
  if (!query) return faqsSorted.value;
  return faqsSorted.value.filter(faq =>
    `${faq.question} ${faq.answer}`.toLowerCase().includes(query)
  );
});

const filteredDocuments = computed(() => {
  const query = docQuery.value.trim().toLowerCase();
  if (!query) return documentsSorted.value;
  return documentsSorted.value.filter(doc =>
    doc.source_ref.toLowerCase().includes(query)
  );
});

// Briefly ring newly added items so the eye lands on them.
const flash = (flashRef, keys) => {
  if (!keys.length) return;
  keys.forEach(key => flashRef.value.add(key));
  flashRef.value = new Set(flashRef.value);
  setTimeout(() => {
    keys.forEach(key => flashRef.value.delete(key));
    flashRef.value = new Set(flashRef.value);
  }, 3000);
};

const fetchAll = async () => {
  loadError.value = false;
  isFetching.value = !knowledgeLoaded.value;
  try {
    const [faqResp, docResp, suggestionResp] = await Promise.all([
      AssistantAPI.getFaqs(),
      AssistantAPI.getDocuments(),
      AssistantAPI.getFaqs('pending'),
    ]);
    faqs.value = faqResp.data;
    documents.value = docResp.data;
    // Defensive filter: an older Brain that ignores the `status` query param
    // would otherwise return ALL approved FAQs here, rendering them as
    // suggestions and allowing Reject to hard-delete a real FAQ.
    suggestions.value = suggestionResp.data.filter(s => s.status === 'pending');
    knowledgeLoaded.value = true;
  } catch (error) {
    loadError.value = true;
  } finally {
    isFetching.value = false;
  }
};

const openFaqDialog = () => {
  faqMode.value = 0;
  faqDialog.value.open();
};

const openDocDialog = () => {
  docMode.value = 0;
  docDialog.value.open();
};

const addFaq = async () => {
  if (!newFaq.value.question || !newFaq.value.answer) return;
  isBusy.value = true;
  const before = new Set(faqs.value.map(faq => faq.id));
  try {
    await AssistantAPI.createFaq({
      question: newFaq.value.question,
      answer: newFaq.value.answer,
    });
    newFaq.value = { question: '', answer: '' };
    await fetchAll();
    flash(
      flashFaqIds,
      faqs.value.map(faq => faq.id).filter(id => !before.has(id))
    );
    useAlert(t('ASSISTANT.KNOWLEDGE.FAQ_ADDED'));
    faqDialog.value.close();
  } catch (error) {
    useAlert(t('ASSISTANT.KNOWLEDGE.SAVE_ERROR'));
  } finally {
    isBusy.value = false;
  }
};

const addBulkFaqs = async () => {
  const pairs = parseBulkFaqs(bulkFaqText.value);
  if (!pairs.length) {
    useAlert(t('ASSISTANT.KNOWLEDGE.BULK_INVALID'));
    return;
  }
  isBusy.value = true;
  const before = new Set(faqs.value.map(faq => faq.id));
  try {
    // sequential on purpose: keeps order and avoids hammering the Brain
    for (let index = 0; index < pairs.length; index += 1) {
      // eslint-disable-next-line no-await-in-loop
      await AssistantAPI.createFaq(pairs[index]);
    }
    bulkFaqText.value = '';
    await fetchAll();
    flash(
      flashFaqIds,
      faqs.value.map(faq => faq.id).filter(id => !before.has(id))
    );
    useAlert(t('ASSISTANT.KNOWLEDGE.BULK_ADDED', { count: pairs.length }));
    faqDialog.value.close();
  } catch (error) {
    useAlert(t('ASSISTANT.KNOWLEDGE.SAVE_ERROR'));
    await fetchAll();
  } finally {
    isBusy.value = false;
  }
};

const onFaqConfirm = () => (faqMode.value === 1 ? addBulkFaqs() : addFaq());

const removeFaq = async id => {
  try {
    await AssistantAPI.deleteFaq(id);
    faqs.value = faqs.value.filter(faq => faq.id !== id);
  } catch (error) {
    useAlert(t('ASSISTANT.KNOWLEDGE.DELETE_ERROR'));
  }
};

const approvingId = ref(null);
const rejectingId = ref(null);

const approveSuggestion = async suggestion => {
  // one approval at a time — a second click would overwrite the id and
  // silently re-enable the first card's button mid-request
  if (approvingId.value) return;
  approvingId.value = suggestion.id;
  try {
    await AssistantAPI.approveFaq(suggestion.id);
    suggestions.value = suggestions.value.filter(s => s.id !== suggestion.id);
    await fetchAll();
    flash(flashFaqIds, [suggestion.id]);
    useAlert(t('ASSISTANT.KNOWLEDGE.SUGGESTION_APPROVED'));
  } catch (error) {
    useAlert(t('ASSISTANT.KNOWLEDGE.SAVE_ERROR'));
  } finally {
    approvingId.value = null;
  }
};

const rejectSuggestion = async suggestion => {
  if (rejectingId.value) return;
  rejectingId.value = suggestion.id;
  try {
    await AssistantAPI.deleteFaq(suggestion.id);
    suggestions.value = suggestions.value.filter(s => s.id !== suggestion.id);
    useAlert(t('ASSISTANT.KNOWLEDGE.SUGGESTION_REJECTED'));
  } catch (error) {
    useAlert(t('ASSISTANT.KNOWLEDGE.DELETE_ERROR'));
  } finally {
    rejectingId.value = null;
  }
};

const extractPdfText = async file => {
  // loaded only when a PDF is actually picked — keeps the main bundle small
  const pdfjs = await import('pdfjs-dist');
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() })
    .promise;
  const pages = [];
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    // eslint-disable-next-line no-await-in-loop
    const page = await pdf.getPage(pageNumber);
    // eslint-disable-next-line no-await-in-loop
    const content = await page.getTextContent();
    pages.push(content.items.map(item => item.str).join(' '));
  }
  return pages.join('\n\n').trim();
};

const handleFileChange = async event => {
  const [file] = event.target.files;
  if (!file) return;
  isReadingFile.value = true;
  try {
    const text = file.name.toLowerCase().endsWith('.pdf')
      ? await extractPdfText(file)
      : (await file.text()).trim();
    if (!text) throw new Error('empty file');
    newDocument.value = { sourceRef: file.name, text };
  } catch (error) {
    newDocument.value = { sourceRef: '', text: '' };
    useAlert(t('ASSISTANT.KNOWLEDGE.FILE_READ_ERROR'));
  } finally {
    isReadingFile.value = false;
    event.target.value = '';
  }
};

const addDocument = async () => {
  if (!newDocument.value.sourceRef || !newDocument.value.text) return;
  isBusy.value = true;
  const before = new Set(documents.value.map(docKey));
  try {
    await AssistantAPI.createDocument({
      sourceRef: newDocument.value.sourceRef,
      text: newDocument.value.text,
    });
    newDocument.value = { sourceRef: '', text: '' };
    await fetchAll();
    const added = documents.value.map(docKey).filter(key => !before.has(key));
    recentDocKeys.value = [...added, ...recentDocKeys.value];
    flash(flashDocKeys, added);
    useAlert(t('ASSISTANT.KNOWLEDGE.DOCUMENT_ADDED'));
    docDialog.value.close();
  } catch (error) {
    useAlert(t('ASSISTANT.KNOWLEDGE.SAVE_ERROR'));
  } finally {
    isBusy.value = false;
  }
};

const startCrawl = async () => {
  if (!websiteUrl.value) return;
  isBusy.value = true;
  try {
    await AssistantAPI.crawl(websiteUrl.value);
    websiteUrl.value = '';
    useAlert(t('ASSISTANT.KNOWLEDGE.CRAWL_STARTED'));
    docDialog.value.close();
  } catch (error) {
    useAlert(t('ASSISTANT.KNOWLEDGE.SAVE_ERROR'));
  } finally {
    isBusy.value = false;
  }
};

const onDocConfirm = () => (docMode.value === 2 ? startCrawl() : addDocument());

const docConfirmLabel = computed(() =>
  docMode.value === 2
    ? t('ASSISTANT.KNOWLEDGE.CRAWL')
    : t('ASSISTANT.KNOWLEDGE.ADD_DOCUMENT')
);

const faqConfirmDisabled = computed(() =>
  faqMode.value === 1
    ? !bulkFaqCount.value
    : !newFaq.value.question || !newFaq.value.answer
);

const docConfirmDisabled = computed(() => {
  if (docMode.value === 2) return !websiteUrl.value;
  return !newDocument.value.sourceRef || !newDocument.value.text;
});

const contentDialog = ref(null);
const viewedDoc = ref(null);
const viewedContent = ref('');
const isLoadingContent = ref(false);
const refreshingDocRef = ref(null);

const viewDocument = async doc => {
  viewedDoc.value = doc;
  viewedContent.value = '';
  isLoadingContent.value = true;
  contentDialog.value.open();
  try {
    const { data } = await AssistantAPI.getDocumentContent({
      sourceType: doc.source_type,
      sourceRef: doc.source_ref,
    });
    viewedContent.value = data.text;
  } catch (error) {
    viewedContent.value = t('ASSISTANT.KNOWLEDGE.CONTENT_LOAD_ERROR');
  } finally {
    isLoadingContent.value = false;
  }
};

const refreshDocument = async doc => {
  refreshingDocRef.value = doc.source_ref;
  try {
    // one-page re-import of exactly this URL; the Brain replaces its chunks
    await AssistantAPI.crawl(doc.source_ref, 1);
    useAlert(t('ASSISTANT.KNOWLEDGE.REFRESH_STARTED'));
  } catch (error) {
    useAlert(t('ASSISTANT.KNOWLEDGE.SAVE_ERROR'));
  } finally {
    refreshingDocRef.value = null;
  }
};

const removeDocument = async doc => {
  try {
    await AssistantAPI.deleteDocument({
      sourceType: doc.source_type,
      sourceRef: doc.source_ref,
    });
    documents.value = documents.value.filter(
      d =>
        !(d.source_type === doc.source_type && d.source_ref === doc.source_ref)
    );
  } catch (error) {
    useAlert(t('ASSISTANT.KNOWLEDGE.DELETE_ERROR'));
  }
};

onMounted(fetchAll);
</script>

<template>
  <AssistantPageLayout
    :header-title="t('ASSISTANT.KNOWLEDGE.TITLE')"
    :description="t('ASSISTANT.KNOWLEDGE.DESCRIPTION')"
    :is-fetching="isFetching"
  >
    <AssistantLoadError
      v-if="loadError"
      class="mb-5"
      :message="t('ASSISTANT.KNOWLEDGE.LOAD_ERROR')"
      @retry="fetchAll"
    />
    <div v-if="knowledgeLoaded" class="flex min-w-0 flex-col gap-5">
      <AssistantSection
        v-if="suggestions.length"
        :heading="t('ASSISTANT.KNOWLEDGE.SUGGESTIONS_TITLE')"
        :description="t('ASSISTANT.KNOWLEDGE.SUGGESTIONS_DESCRIPTION')"
        :count="suggestions.length"
        icon="i-lucide-list-checks"
      >
        <div class="divide-y divide-n-weak">
          <article
            v-for="suggestion in suggestions"
            :key="suggestion.id"
            class="flex min-w-0 flex-col gap-4 p-4 sm:flex-row sm:px-5"
          >
            <div class="min-w-0 flex-1">
              <h3
                class="mb-2 text-sm font-medium leading-6 text-n-slate-12 break-words"
              >
                {{ suggestion.question }}
              </h3>
              <p
                class="mb-2 max-w-prose whitespace-pre-line break-words text-sm leading-6 text-n-slate-11"
              >
                {{ suggestion.answer }}
              </p>
              <span
                v-if="suggestion.source_conversation_id"
                class="text-xs text-n-slate-11"
              >
                {{
                  t('ASSISTANT.KNOWLEDGE.SUGGESTION_SOURCE', {
                    id: suggestion.source_conversation_id,
                  })
                }}
              </span>
            </div>
            <div class="flex shrink-0 items-start gap-1">
              <Button
                :label="t('ASSISTANT.KNOWLEDGE.APPROVE')"
                icon="i-lucide-check"
                size="sm"
                class="min-h-11"
                :is-loading="approvingId === suggestion.id"
                :disabled="approvingId !== null"
                @click="approveSuggestion(suggestion)"
              />
              <Button
                ghost
                ruby
                size="sm"
                icon="i-lucide-trash"
                class="min-h-11 min-w-11"
                :title="t('ASSISTANT.KNOWLEDGE.REJECT')"
                :aria-label="`${t('ASSISTANT.KNOWLEDGE.REJECT')}: ${suggestion.question}`"
                :is-loading="rejectingId === suggestion.id"
                :disabled="rejectingId !== null"
                @click="rejectSuggestion(suggestion)"
              />
            </div>
          </article>
        </div>
      </AssistantSection>

      <AssistantSection
        :heading="t('ASSISTANT.KNOWLEDGE.FAQ_TITLE')"
        :description="t('ASSISTANT.KNOWLEDGE.FAQ_DESCRIPTION')"
        :count="faqs.length"
        icon="i-lucide-messages-square"
      >
        <template #actions>
          <Button
            :label="t('ASSISTANT.KNOWLEDGE.ADD_FAQ')"
            icon="i-lucide-plus"
            size="sm"
            class="min-h-11"
            @click="openFaqDialog"
          />
        </template>
        <div v-if="faqs.length" class="border-b border-n-weak p-4 sm:px-5">
          <Input
            v-model="faqQuery"
            :placeholder="t('ASSISTANT.KNOWLEDGE.SEARCH_FAQS')"
            :label="t('ASSISTANT.KNOWLEDGE.SEARCH_FAQS')"
          />
        </div>
        <div
          v-if="!faqs.length"
          class="flex flex-col items-center gap-3 px-4 py-8 text-center"
        >
          <p class="mb-0 max-w-sm text-sm leading-6 text-n-slate-11">
            {{ t('ASSISTANT.KNOWLEDGE.EMPTY_FAQS') }}
          </p>
          <Button
            :label="t('ASSISTANT.KNOWLEDGE.ADD_FIRST_FAQ')"
            icon="i-lucide-plus"
            size="sm"
            color="slate"
            class="min-h-11"
            @click="openFaqDialog"
          />
        </div>
        <p
          v-else-if="!filteredFaqs.length"
          class="mb-0 p-5 text-sm text-n-slate-11"
          role="status"
        >
          {{ t('ASSISTANT.KNOWLEDGE.NO_FAQ_RESULTS') }}
        </p>
        <div v-else class="divide-y divide-n-weak">
          <article
            v-for="faq in filteredFaqs"
            :key="faq.id"
            class="flex min-w-0 items-start gap-3 p-4 transition-colors sm:px-5"
            :class="flashFaqIds.has(faq.id) ? 'bg-n-brand/5' : ''"
          >
            <div class="min-w-0 flex-1">
              <h3
                class="mb-2 text-sm font-medium leading-6 break-words text-n-slate-12"
              >
                {{ faq.question }}
                <span
                  v-if="flashFaqIds.has(faq.id)"
                  class="ms-2 rounded px-1.5 py-0.5 text-xs font-normal bg-n-brand/10 text-n-brand"
                >
                  {{ t('ASSISTANT.KNOWLEDGE.NEW_BADGE') }}
                </span>
              </h3>
              <p
                class="mb-0 max-w-prose whitespace-pre-line break-words text-sm leading-6 text-n-slate-11"
              >
                {{ faq.answer }}
              </p>
            </div>
            <Button
              ghost
              ruby
              size="sm"
              icon="i-lucide-trash"
              class="min-h-11 min-w-11 shrink-0"
              :title="t('ASSISTANT.TOOLS.COMMON.DELETE')"
              :aria-label="`${t('ASSISTANT.TOOLS.COMMON.DELETE')}: ${faq.question}`"
              @click="removeFaq(faq.id)"
            />
          </article>
        </div>
      </AssistantSection>

      <AssistantSection
        :heading="t('ASSISTANT.KNOWLEDGE.DOCUMENTS_TITLE')"
        :description="t('ASSISTANT.KNOWLEDGE.DOCUMENTS_DESCRIPTION')"
        :count="documents.length"
        icon="i-lucide-files"
      >
        <template #actions>
          <Button
            :label="t('ASSISTANT.KNOWLEDGE.ADD_DOCUMENT')"
            icon="i-lucide-plus"
            size="sm"
            class="min-h-11"
            @click="openDocDialog"
          />
        </template>
        <div v-if="documents.length" class="border-b border-n-weak p-4 sm:px-5">
          <Input
            v-model="docQuery"
            :placeholder="t('ASSISTANT.KNOWLEDGE.SEARCH_DOCUMENTS')"
            :label="t('ASSISTANT.KNOWLEDGE.SEARCH_DOCUMENTS')"
          />
        </div>
        <div
          v-if="!documents.length"
          class="flex flex-col items-center gap-3 px-4 py-8 text-center"
        >
          <p class="mb-0 text-sm leading-6 text-n-slate-11">
            {{ t('ASSISTANT.KNOWLEDGE.EMPTY_DOCUMENTS') }}
          </p>
          <Button
            :label="t('ASSISTANT.KNOWLEDGE.ADD_FIRST_DOCUMENT')"
            icon="i-lucide-plus"
            size="sm"
            color="slate"
            class="min-h-11"
            @click="openDocDialog"
          />
        </div>
        <p
          v-else-if="!filteredDocuments.length"
          class="mb-0 p-5 text-sm text-n-slate-11"
          role="status"
        >
          {{ t('ASSISTANT.KNOWLEDGE.NO_DOCUMENT_RESULTS') }}
        </p>
        <div v-else class="divide-y divide-n-weak">
          <article
            v-for="doc in filteredDocuments"
            :key="docKey(doc)"
            class="flex min-w-0 flex-col gap-3 p-4 transition-colors sm:flex-row sm:items-center sm:px-5"
            :class="flashDocKeys.has(docKey(doc)) ? 'bg-n-brand/5' : ''"
          >
            <div class="flex min-w-0 flex-1 items-start gap-3">
              <Icon
                :icon="sourceIcon(doc.source_type)"
                class="mt-1 size-5 shrink-0 text-n-slate-11"
                aria-hidden="true"
              />
              <div class="min-w-0 flex-1">
                <h3
                  class="mb-1 break-all text-sm font-medium leading-6 text-n-slate-12"
                >
                  <bdi>{{ doc.source_ref }}</bdi>
                </h3>
                <span class="text-xs tabular-nums text-n-slate-11">
                  {{ doc.chunks }} {{ t('ASSISTANT.KNOWLEDGE.CHUNKS') }}
                </span>
                <span
                  v-if="flashDocKeys.has(docKey(doc))"
                  class="ms-2 rounded px-1.5 py-0.5 text-xs bg-n-brand/10 text-n-brand"
                >
                  {{ t('ASSISTANT.KNOWLEDGE.NEW_BADGE') }}
                </span>
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-1">
              <Button
                ghost
                slate
                size="sm"
                icon="i-lucide-eye"
                class="min-h-11 min-w-11"
                :title="t('ASSISTANT.KNOWLEDGE.VIEW')"
                :aria-label="`${t('ASSISTANT.KNOWLEDGE.VIEW')}: ${doc.source_ref}`"
                @click="viewDocument(doc)"
              />
              <Button
                v-if="doc.source_type === 'website'"
                ghost
                slate
                size="sm"
                icon="i-lucide-refresh-cw"
                class="min-h-11 min-w-11"
                :title="t('ASSISTANT.KNOWLEDGE.REFRESH')"
                :aria-label="`${t('ASSISTANT.KNOWLEDGE.REFRESH')}: ${doc.source_ref}`"
                :is-loading="refreshingDocRef === doc.source_ref"
                :disabled="refreshingDocRef === doc.source_ref"
                @click="refreshDocument(doc)"
              />
              <Button
                ghost
                ruby
                size="sm"
                icon="i-lucide-trash"
                class="min-h-11 min-w-11"
                :title="t('ASSISTANT.TOOLS.COMMON.DELETE')"
                :aria-label="`${t('ASSISTANT.TOOLS.COMMON.DELETE')}: ${doc.source_ref}`"
                @click="removeDocument(doc)"
              />
            </div>
          </article>
        </div>
      </AssistantSection>
    </div>
    <!-- Pictures load on their own, so a FAQ/document failure does not hide them. -->
    <PictureLibrary :class="{ 'mt-5': knowledgeLoaded }" />

    <!-- Add FAQ dialog -->
    <Dialog
      ref="faqDialog"
      :title="t('ASSISTANT.KNOWLEDGE.ADD_FAQ')"
      :description="t('ASSISTANT.KNOWLEDGE.FAQ_DESCRIPTION')"
      :confirm-button-label="
        faqMode === 1
          ? t('ASSISTANT.KNOWLEDGE.ADD_BULK')
          : t('ASSISTANT.KNOWLEDGE.ADD_FAQ')
      "
      :is-loading="isBusy"
      :disable-confirm-button="faqConfirmDisabled"
      width="2xl"
      @confirm="onFaqConfirm"
    >
      <AssistantFormTabs
        v-model="faqMode"
        :aria-label="t('ASSISTANT.KNOWLEDGE.ADD_FAQ')"
        :tabs="faqTabs"
      />

      <template v-if="faqMode === 0">
        <Input
          v-model="newFaq.question"
          :label="t('ASSISTANT.KNOWLEDGE.QUESTION')"
        />
        <TextArea
          id="knowledge-faq-answer"
          v-model="newFaq.answer"
          :label="t('ASSISTANT.KNOWLEDGE.ANSWER')"
          auto-height
        />
      </template>

      <template v-else>
        <TextArea
          id="knowledge-faq-bulk"
          v-model="bulkFaqText"
          :label="t('ASSISTANT.KNOWLEDGE.BULK_LABEL')"
          :placeholder="t('ASSISTANT.KNOWLEDGE.BULK_PLACEHOLDER')"
          auto-height
          min-height="10rem"
          max-height="20rem"
        />
        <p class="text-sm text-n-slate-11">{{ bulkHintText }}</p>
      </template>
    </Dialog>

    <!-- Add document dialog -->
    <Dialog
      ref="docDialog"
      :title="t('ASSISTANT.KNOWLEDGE.ADD_DOCUMENT')"
      :description="t('ASSISTANT.KNOWLEDGE.DOCUMENTS_DESCRIPTION')"
      :confirm-button-label="docConfirmLabel"
      :is-loading="isBusy"
      :disable-confirm-button="docConfirmDisabled"
      width="2xl"
      @confirm="onDocConfirm"
    >
      <AssistantFormTabs
        v-model="docMode"
        :aria-label="t('ASSISTANT.KNOWLEDGE.ADD_DOCUMENT')"
        :tabs="docTabs"
      />

      <template v-if="docMode === 0">
        <input
          ref="fileInput"
          type="file"
          accept=".txt,.md,.markdown,.pdf,text/plain,text/markdown,application/pdf"
          class="hidden"
          @change="handleFileChange"
        />
        <div
          class="flex flex-col items-start gap-3 sm:flex-row sm:items-center"
        >
          <Button
            type="button"
            :label="t('ASSISTANT.KNOWLEDGE.UPLOAD_FILE')"
            icon="i-lucide-upload"
            size="sm"
            color="slate"
            class="min-h-11 shrink-0"
            :is-loading="isReadingFile"
            :disabled="isReadingFile"
            @click="fileInput.click()"
          />
          <span class="text-sm text-n-slate-11">
            {{ t('ASSISTANT.KNOWLEDGE.UPLOAD_HINT') }}
          </span>
        </div>
        <template v-if="newDocument.text">
          <Input
            v-model="newDocument.sourceRef"
            :label="t('ASSISTANT.KNOWLEDGE.DOCUMENT_NAME')"
          />
          <span class="flex items-center gap-1 text-sm text-n-slate-11">
            <Icon icon="i-ph-file-text" class="shrink-0" />
            {{
              t('ASSISTANT.KNOWLEDGE.FILE_READY', {
                chars: newDocument.text.length,
              })
            }}
          </span>
        </template>
      </template>

      <template v-else-if="docMode === 1">
        <Input
          v-model="newDocument.sourceRef"
          :label="t('ASSISTANT.KNOWLEDGE.DOCUMENT_NAME')"
          :placeholder="t('ASSISTANT.KNOWLEDGE.DOCUMENT_NAME_PLACEHOLDER')"
        />
        <TextArea
          id="knowledge-document-text"
          v-model="newDocument.text"
          :label="t('ASSISTANT.KNOWLEDGE.DOCUMENT_TEXT')"
          auto-height
          min-height="8rem"
          max-height="20rem"
        />
      </template>

      <template v-else>
        <p class="text-sm text-n-slate-11">
          {{ t('ASSISTANT.KNOWLEDGE.WEBSITE_DESCRIPTION') }}
        </p>
        <Input
          v-model="websiteUrl"
          type="url"
          :label="t('ASSISTANT.KNOWLEDGE.WEBSITE_URL')"
          :placeholder="t('ASSISTANT.KNOWLEDGE.WEBSITE_URL_PLACEHOLDER')"
        />
      </template>
    </Dialog>

    <!-- View document content -->
    <Dialog
      ref="contentDialog"
      :title="t('ASSISTANT.KNOWLEDGE.VIEW')"
      :description="t('ASSISTANT.KNOWLEDGE.CONTENT_DESCRIPTION')"
      width="2xl"
      overflow-y-auto
      :show-confirm-button="false"
      :cancel-button-label="t('ASSISTANT.KNOWLEDGE.CLOSE')"
    >
      <p
        v-if="viewedDoc"
        class="mb-0 break-all text-sm font-medium text-n-slate-12"
      >
        <bdi>{{ viewedDoc.source_ref }}</bdi>
      </p>
      <p v-if="isLoadingContent" class="text-sm text-n-slate-11">
        {{ t('ASSISTANT.KNOWLEDGE.CONTENT_LOADING') }}
      </p>
      <p
        v-else
        class="text-sm text-n-slate-11 whitespace-pre-wrap break-words max-h-96 overflow-y-auto"
      >
        {{ viewedContent }}
      </p>
    </Dialog>
  </AssistantPageLayout>
</template>
